import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUserData } from "../../context/UserDataFetcher";
import { useUsersByManufacturer } from "../../context/UsersDataFetcher";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fade,
  Fab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditUserModal, { FormUserType } from "./EditUserModal";
import UpdatePasswordModal from "./UpdatePasswordModal";
import LockIcon from "@mui/icons-material/Lock";
import type { User } from "../../types/DB2";
import { useTheme } from "@emotion/react";
import { CustomThemeType } from "../../context/Providers";
import DashboardLayout from "../../layouts/DashboardLayout";
import UserIcon from "@mui/icons-material/Person";
import { th } from "zod/v4/locales/index.cjs";

const DEFAULT_ROLE_OPTIONS = ["manufacturer", "dealer", "driver", "manufacturerRep"];
const DEALER_ROLE_OPTIONS = ["dealer", "driver"] as const;
const DEALER_ROLE_SET = new Set<string>(DEALER_ROLE_OPTIONS);
const ROLE_LABEL_MAP: Record<string, string> = {
  manufacturer: "Manufacturer",
  dealer: "Dealer",
  driver: "Driver",
  manufacturerRep: "Rep",
  admin: "Admin",
};

const AdminDashboard: React.FC = () => {
  // pull data
  const userData = useUserData();
  const {
    users,
    refresh,
    createUser,
    deleteUsers,
    updateUserPasswords,
    updateUsers,
  } = useUsersByManufacturer();
  const theme: CustomThemeType = useTheme() as CustomThemeType;

  // state
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  // bulk
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [bulkSelectedEmails, setBulkSelectedEmails] = useState<string[]>([]);
  // password
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState<User | null>(null);
  // action menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<User | null>(null);
  // delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);
  // bulk delete
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const isDealerScope =
    userData?.role === "dealer" && !userData?.isAdmin && !userData?.isDev;

  // effects
  // load
  useEffect(() => {
    if (hasLoaded) return;
    refresh()
      .then(() => setHasLoaded(true))
      .catch(() => setHasLoaded(true));
  }, [hasLoaded, refresh]);
  useEffect(() => {
    setHasLoaded(false);
  }, [userData?.manufacturerId]);

  const openDeleteConfirm = (u: User) => {
    console.log(u);
    setDeletingUser(u);
    setDeleteConfirmOpen(true);
    closeMenu();
  };
  const closeDeleteConfirm = () => {
    setDeletingUser(null);
    setDeleteConfirmOpen(false);
  };
  const handleDeleteConfirm = async () => {
    if (!deletingUser) return;
    setDeleting(true);
    try {
      // call deleteUsers cloud function (pass email)
      if (!deletingUser.email) {
        console.error("handleDeleteConfirm: missing user email", deletingUser);
      } else {
        const res = await deleteUsers([deletingUser.email]);
        console.log("deleteUsers result:", res);
      }
      await refresh();
    } catch {
      /* ignore */
    } finally {
      setDeleting(false);
      closeDeleteConfirm();
    }
  };

  const openBulkDeleteConfirm = () => {
    setBulkDeleteConfirmOpen(true);
  };
  const closeBulkDeleteConfirm = () => {
    setBulkDeleteConfirmOpen(false);
  };

  const handleBulkDeleteConfirm = async () => {
    // collect selected rows' emails
    const selectedEmails = table
      .getSelectedRowModel()
      .rows.map((r) => (r.original as User).email)
      .filter(Boolean) as string[];

    if (!selectedEmails.length) {
      closeBulkDeleteConfirm();
      return;
    }

    setBulkDeleting(true);
    try {
      const res = await deleteUsers(selectedEmails);
      console.log("bulk deleteUsers result:", res);
      await refresh();
    } catch (err) {
      console.error("handleBulkDeleteConfirm error:", err);
    } finally {
      setBulkDeleting(false);
      closeBulkDeleteConfirm();
    }
  };

  const canManageUsers =
    userData?.isAdmin ||
    userData?.isDev ||
    userData?.isManufacturerPrincipal ||
    userData?.role === "dealer";

  if (!canManageUsers) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Not authorized</h2>
        <p>You must be authorized to manage users.</p>
      </div>
    );
  }

  const roleOptions = useMemo(() => {
    if (isDealerScope) {
      return Array.from(DEALER_ROLE_SET);
    }
    const set = new Set<string>(DEFAULT_ROLE_OPTIONS);
    (users ?? []).forEach((u) => {
      if (u.role) {
        set.add(u.role);
      }
    });
    if (userData?.role) {
      set.add(userData.role);
    }
    return Array.from(set);
  }, [isDealerScope, users, userData?.role]);

  const formatRole = useCallback((role?: string) => {
    if (!role) return "—";
    if (ROLE_LABEL_MAP[role]) return ROLE_LABEL_MAP[role];
    return role
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 220,
        muiTableHeadCellProps: { sx: { px: 1 } },
        muiTableBodyCellProps: { sx: { px: 1 } },
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 240,
        muiTableHeadCellProps: { sx: { px: 1 } },
        muiTableBodyCellProps: { sx: { px: 1 } },
      },
      {
        accessorKey: "role",
        header: "Role",
        Cell: ({ cell }: any) => formatRole(cell.getValue()),
        size: 160,
        muiTableHeadCellProps: { sx: { px: 1 } },
        muiTableBodyCellProps: { sx: { px: 1 } },
      },
      {
        accessorKey: "isAdmin",
        header: "Admin",
        Cell: ({ cell }: any) => (!!cell.getValue() ? "Yes" : "No"),
        size: 120,
        muiTableHeadCellProps: {
          sx: {
            px: 1,
            "& > div": {
              justifyContent: "center",
            },
          },
        },
        muiTableBodyCellProps: {
          sx: {
            px: 1,
            textAlign: "center",
          },
        },
      },
    ],
    [formatRole]
  );

  const openAdd = () => {
    setEditingUser(null);
    setIsBulkEdit(false);
    setModalOpen(true);
  };

  const openEdit = (u: User) => {
    setEditingUser(u);
    setIsBulkEdit(false);
    setModalOpen(true);
  };

  // NEW: open bulk edit modal - collects selected emails and opens modal in bulk mode
  const openEditSelected = () => {
    const selectedEmails = table
      .getSelectedRowModel()
      .rows.map((r) => (r.original as User).email)
      .filter(Boolean) as string[];

    if (!selectedEmails.length) {
      // nothing selected
      return;
    }

    setBulkSelectedEmails(selectedEmails);
    setEditingUser(null);
    setIsBulkEdit(true);
    setModalOpen(true);
  };

  const openMenu = (u: User, event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuUser(u);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuUser(null);
  };

  const openUpdatePassword = (u: User) => {
    setPasswordUser(u);
    setPasswordModalOpen(true);
    closeMenu();
  };


  // HANDLE SAVE
  const handleSave = async (u: FormUserType) => {
    if (!userData) {
      console.log("not signed in");
      return;
    }

    const normalizeDealerRole = (value?: string | null) => {
      if (!value || typeof value !== "string") return "dealer";
      const normalized = value.trim().toLowerCase();
      return DEALER_ROLE_SET.has(normalized) ? normalized : "dealer";
    };
    const isDriverRoleValue = (value?: string | null) =>
      typeof value === "string" && value.trim().toLowerCase() === "driver";

    // BULK EDIT
    if (isBulkEdit) {

      const isUpdatingPassword =
        typeof u.password !== "undefined" && u.password !== "";
      if (isUpdatingPassword) {
        const passwordUpdates = bulkSelectedEmails.map((email) => ({
          email,
          password: u.password!,
        }));
        try {
          await updateUserPasswords(passwordUpdates);
        } catch (err) {
          console.error("handleSave (bulk) error:", err);
        }
      }


      const allowedFields: Array<keyof FormUserType> = [
        "name",
        "role",
        "isAdmin",
        "profilePicUrl",
        "email",
      ];
      const fields: Record<string, any> = {};
      allowedFields.forEach((field) => {
        if (
          Object.prototype.hasOwnProperty.call(u, field) &&
          u[field] !== undefined
        ) {
          fields[field] = u[field];
        }
      });
      if (isDealerScope) {
        if (fields.role !== undefined) {
          fields.role = normalizeDealerRole(String(fields.role));
        }
        if (Object.prototype.hasOwnProperty.call(fields, "isAdmin")) {
          fields.isAdmin = false;
        }
      }
      if (isDriverRoleValue(fields.role)) {
        fields.isAdmin = false;
      }
      // guard
      if (!Object.keys(fields).length) {
        console.error("handleSave (bulk): no fields to update");
        setModalOpen(false);
        setIsBulkEdit(false);
        setBulkSelectedEmails([]);
        return;
      }

      const updates = bulkSelectedEmails.map((email) => ({ email, fields }));

      try {
        const res = await updateUsers(updates);
        console.log("bulk updateUsers result:", res);
        await refresh();
      } catch (err) {
        console.error("handleSave (bulk) error:", err);
      } finally {
        setModalOpen(false);
        setIsBulkEdit(false);
        setBulkSelectedEmails([]);
      }

      setBulkSelectedEmails([]);

      return;
    }

    setBulkSelectedEmails([]);
    // SINGLE
    try {
      // CREATE
      if (!editingUser) {
        if (!u.email || !u.password || !u.name) {
          console.error("handleSave: missing required fields for create", u);
          return;
        }
        if (isDealerScope && !userData.dealerId) {
          console.error("handleSave: dealer scope missing dealerId");
          return;
        }
        const createRole = isDealerScope
          ? normalizeDealerRole(u.role)
          : u.role ?? userData.role ?? "dealer";
        const allowAdminForCreate =
          !isDealerScope && !isDriverRoleValue(createRole);
        await createUser({
          email: u.email ?? "",
          password: (u as any).password,
          role: createRole,
          dealerId: userData.dealerId || undefined,
          manufacturerId: isDealerScope
            ? undefined
            : userData.manufacturerId || undefined,
          isAdmin: allowAdminForCreate ? !!u.isAdmin : false,
          name: u.name || "",
        });
        await refresh();
        setModalOpen(false);
        return;

        // EDIT
      } else if (editingUser) {
        const fieldsToUpdate: Record<string, any> = {
          ...(u as Record<string, any>),
        };
        const password = fieldsToUpdate.password as string | undefined;
        if (password !== undefined) {
          delete fieldsToUpdate.password;
        }
        if (isDealerScope) {
          if (fieldsToUpdate.role !== undefined) {
            fieldsToUpdate.role = normalizeDealerRole(fieldsToUpdate.role);
          }
          if (userData.dealerId) {
            fieldsToUpdate.dealerId = userData.dealerId;
          } else if (Object.prototype.hasOwnProperty.call(fieldsToUpdate, "dealerId")) {
            delete fieldsToUpdate.dealerId;
          }
          fieldsToUpdate.isAdmin = false;
          if (fieldsToUpdate.manufacturerId !== undefined) {
            delete fieldsToUpdate.manufacturerId;
          }
        }
        const resultingRole =
          (typeof fieldsToUpdate.role === "string"
            ? fieldsToUpdate.role
            : editingUser.role) ?? undefined;
        if (isDriverRoleValue(resultingRole)) {
          if (Object.prototype.hasOwnProperty.call(fieldsToUpdate, "isAdmin")) {
            fieldsToUpdate.isAdmin = false;
          } else if (editingUser.isAdmin) {
            fieldsToUpdate.isAdmin = false;
          }
        }
        const hasFields = Object.keys(fieldsToUpdate).length > 0;

        // guard
        if (!hasFields && !password) {
          console.error("handleSave: no changes to apply");
          return;
        }

        const identifierEmail = editingUser.email;
        if (hasFields) {
          try {
            const res = await updateUsers([
              { email: identifierEmail, fields: fieldsToUpdate },
            ]);
            console.log("updateUsers (single) result:", res);
          } catch (err) {
            console.error("updateUsers (single) error:", err);
            throw err;
          }
        }
        if (password) {
          try {
            const res = await updateUserPasswords([
              { email: identifierEmail, password },
            ]);
            console.log("updateUserPasswords (single) result:", res);
          } catch (err) {
            console.error("updateUserPasswords (single) error:", err);
            throw err;
          }
        }
      }

      await refresh();
      setModalOpen(false);
    } catch (err) {
      console.error("handleSave error:", err);
    }
  };

  const handlePasswordSave = async (userEmail: string, newPassword: string) => {
    if (!userEmail || !newPassword) return;
    try {
      const res = await updateUserPasswords([
        { email: userEmail, password: newPassword },
      ]);
      console.log("updateUserPasswords result:", res);
      await refresh();
    } catch (err) {
      console.error("handlePasswordSave error:", err);
    } finally {
      setPasswordModalOpen(false);
      setPasswordUser(null);
    }
  };

  const table = useMaterialReactTable({
    columns,
    data: users ?? [],
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableBottomToolbar: true,
    positionToolbarAlertBanner: "none",
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableRowActions: true,
    positionActionsColumn: "last",
    displayColumnDefOptions: {
      "mrt-row-actions": {
        header: "",
        muiTableHeadCellProps: (theme) => ({
          sx: { width: 56, maxWidth: 56, minWidth: 56, px: 1 },
        }),
        muiTableBodyCellProps: {
          sx: { width: 56, maxWidth: 56, minWidth: 56, px: 1 },
        },
      },
      "mrt-row-select": {
        header: "",
        muiTableHeadCellProps: {
          sx: { width: 128, maxWidth: 128, minWidth: 128, px: 1 },
        },
        muiTableBodyCellProps: {
          sx: { width: 48, maxWidth: 48, minWidth: 48, px: 1 },
        },
      },
    },
    muiTableProps: {
      sx: (theme) => ({
        elevation: 0,
        boxShadow: "none",
      }),
    },
    muiTableHeadRowProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.paper,
        "& th": {
          borderBottom: `1px solid ${theme.palette.divider} !important`,
        },
        "&:hover": {
          backgroundColor: `${theme.palette.background.paper} !important`,
        },
      }),
    },
    muiTableBodyRowProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.paper,
        "&:hover": {
          cursor: "pointer",
          backgroundColor: theme.palette.action.hover,
        },
        "&.Mui-selected": {
          backgroundColor: theme.palette.action.selected,
        },
        "& td": {
          borderBottom: `1px solid ${theme.palette.divider} !important`,
        },
        "& td:after": {
          backgroundColor: 'transparent !important',
        }
      }),
    },
    muiTableFooterProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
    muiTableFooterRowProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
    muiBottomToolbarProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
    muiTablePaperProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.default, // use surface background
      }),
    },
    muiTableBodyProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
    muiTableHeadProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
    muiTableBodyCellProps: {
      sx: (theme) => ({
        borderBottom: '1px solid black !important',
        border: '1px solid black !important'
      }),
    },

    renderRowActions: ({ row }) => (
      <IconButton
        size="small"
        onClick={(e) => openMenu(row.original as User, e)}
        aria-controls={menuAnchorEl ? "row-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={Boolean(menuAnchorEl) ? "true" : undefined}
      >
        <MoreVertIcon />
      </IconButton>
    ),
    muiTopToolbarProps: {
      sx: (theme) => ({
        display: "flex",
        alignItems: "center",
        backgroundColor: theme.palette.background.paper,
        "& > div": {
          flexDirection: 'row-reverse'
        }
      }),
    },
    renderTopToolbarCustomActions: ({ table: _table }) => (
      <IconButton color="primary" onClick={openAdd}>
        <AddIcon />
      </IconButton>
    ),
    initialState: { density: "spacious", pagination: { pageIndex: 0, pageSize: 25 } },
  });

  const selectionCount = table.getSelectedRowModel().rows.length;

  return (
    <>
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
          <Box sx={{ width: { xs: "100%", lg: "100%" }, maxWidth: "1400px" }}>
            <Box
              sx={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "primary.main",
                }}
              >
                <UserIcon
                  sx={{
                    fontSize: "2.5rem",
                    bottom: "2px",
                    position: "relative",
                  }}
                  color="primary"
                />
                Manage Users
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  opacity: selectionCount > 0 ? 1 : 0,
                  transition: "opacity 0.3s ease",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={openBulkDeleteConfirm}
                  startIcon={<DeleteIcon />}
                >
                  Delete Selected
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={openEditSelected} // changed to bulk edit handler
                  startIcon={<EditIcon />}
                >
                  Edit Selected
                </Button>
              </Box>
            </Box>
            <MaterialReactTable table={table} />
          </Box>
        </Box>

        <Menu
          id="row-actions-menu"
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              if (menuUser) {
                openEdit(menuUser);
                closeMenu();
              } else closeMenu();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit User</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuUser) openUpdatePassword(menuUser);
              else closeMenu();
            }}
          >
            <ListItemIcon>
              <LockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Update Password</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuUser) openDeleteConfirm(menuUser);
              else closeMenu();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete User</ListItemText>
          </MenuItem>
        </Menu>

        <Dialog
          open={deleteConfirmOpen}
          onClose={closeDeleteConfirm}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete{" "}
              <strong>
                {deletingUser
                  ? (deletingUser.email ?? deletingUser.name ?? deletingUser.id)
                  : ""}
              </strong>
              ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteConfirm} disabled={deleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={bulkDeleteConfirmOpen}
          onClose={closeBulkDeleteConfirm}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Selected Users</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the selected{" "}
              <strong>{selectionCount}</strong> user(s)? This action cannot be
              undone.
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="textSecondary">
                The following emails will be deleted:
              </Typography>
              <Box sx={{ maxHeight: 160, overflow: "auto", mt: 1 }}>
                {table.getSelectedRowModel().rows.map((r) => (
                  <Typography key={(r.original as User).id} variant="body2">
                    {(r.original as User).email}
                  </Typography>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeBulkDeleteConfirm} disabled={bulkDeleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleBulkDeleteConfirm}
              disabled={bulkDeleting}
            >
              {bulkDeleting ? "Deleting…" : "Delete Selected"}
            </Button>
          </DialogActions>
        </Dialog>

        <EditUserModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setIsBulkEdit(false);
            setBulkSelectedEmails([]);
          }}
          onSave={handleSave}
          user={editingUser ?? undefined}
          bulk={isBulkEdit}
          roleOptions={roleOptions}
        />

        <UpdatePasswordModal
          open={passwordModalOpen}
          onClose={() => {
            setPasswordModalOpen(false);
            setPasswordUser(null);
          }}
          user={passwordUser ?? undefined}
          onSave={handlePasswordSave}
        />

        {/* Floating action button that fades in when one or more rows are selected (will bring back when made responsive)*/}
        {/* <Fade in={selectionCount > 0} timeout={300} unmountOnExit> */}
        <Fade in={false} timeout={300} unmountOnExit>
          <Box>
            {/* Edit bulk FAB (existing) */}
            <Fab
              aria-label="bulk-actions"
              onClick={() => {
                const ids = table
                  .getSelectedRowModel()
                  .rows.map((r) => (r.original as User).id);
                console.log("Bulk action on selected:", ids);
                // TODO: perform bulk action (e.g., open bulk action dialog)
              }}
              // 2.5x the medium FAB (56px * 2.5 = 140px)
              sx={{
                position: "fixed",
                bottom: 24,
                right: 24,
                zIndex: 1400,
                width: 100,
                height: 100,
                bgcolor: theme.brand,
                color: "#fff",
                "&:hover": { bgcolor: theme.brand },
                mb: 1,
              }}
            >
              <EditIcon sx={{ fontSize: 32 }} />
            </Fab>

            {/* Delete bulk FAB (new) */}
            <Fab
              aria-label="bulk-delete"
              color="error"
              onClick={openBulkDeleteConfirm}
              sx={{
                position: "fixed",
                bottom: 24,
                right: 140,
                zIndex: 1400,
                width: 100,
                height: 100,
                bgcolor: "#d32f2f",
                color: "#fff",
                "&:hover": { bgcolor: "#b71c1c" },
              }}
            >
              <DeleteIcon sx={{ fontSize: 32 }} />
            </Fab>
          </Box>
        </Fade>
      </DashboardLayout>
    </>
  );
};

export default AdminDashboard;
